import b from 'b_';
import { hideNotification as hideNotificationAction, Modal, type SimpleCallback } from 'platform-components';
import {
    type InjectedProps as WithRouterPathProps,
    withRouterPath,
} from 'platform-components/src/utils/hocs/with-router-path/with-router-path';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router';
import { useStableCallback } from 'platform-components/src/utils/hooks';
import { usePrevious } from 'react-use';
import ym from 'react-yandex-metrika';
import qs from 'query-string';
import { type Action, type Dispatch, bindActionCreators } from 'redux';

import * as certificatesActions from 'common.components/certificates/actions';
import { LIBRARY_CONTENT_TYPE } from 'common.components/constants';
import { useViewportHack } from 'common.components/hooks';
import { LessonSocket } from 'common.components/lesson-socket/lesson-socket';
import * as lessonAssignmentActions from 'common.components/lesson-view.assignment/actions';
import LessonNotification from 'common.components/lesson-view.index/__notification/lesson-view__notification';
import SidePanel from 'common.components/lesson-view.side-panel/side-panel';
import { setIsHeadless } from 'common.components/lesson-view.store/actions';
import * as lessonViewActions from 'common.components/lesson-view.store/actions';
import { actions as lessonActions } from 'common.components/lesson';
import * as lessonViewSelectors from 'common.components/lesson-view.store/selectors';
import * as lessonSelectors from 'common.components/lesson/selectors';
import { isLessonCompleted } from 'common.components/lesson/utils';
import { resultsActions } from 'common.components/results/actions';
import * as resultsSelectors from 'common.components/results/selectors';
import { actions as problemsViewActions } from 'store/problems-view/actions';

import { sendMessageFromIFrame } from 'utils/send-from-iframe';
import Content from './__content/lesson-view__content';
import * as selectors from './selectors';

import './lesson-view.scss';

interface OwnProps extends WithRouterPathProps {
    /**
     * Нужно ли скрыть крестик
     */
    hideCloseButton?: boolean;

    /**
     * Можно ли копировать занятие в другой класс
     */
    isCopyingEnabled?: boolean;

    /**
     * Нужно ли скрыть футер (action-кнопку и троеточие)
     */
    hideFooter?: boolean;

    /**
     * Можно ли вывести на доску или посмотреть как ученик
     */
    isPutOnBoardEnabled?: boolean;

    /**
     * Закрывать ли модалку, если пользователь кликнул браузерную "назад"
     */
    shouldCloseOnGoBack?: boolean;

    /**
     * Показывать ли заглушку вместо результатов
     */
    shouldShowResultsStub?: boolean;

    /**
     * Нужно ли скрывать баннеры с работой над ошибками и рекомендациями
     */
    hideBanners?: boolean;

    onClose?: SimpleCallback;
    onForceComplete?: () => void;

    /** включает загрузку урока и открытие модалки при наличии lesson_id в query параметрах */
    enableOpeningModalFromQuery?: boolean;
}

type StoreProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;

export interface Props extends OwnProps, StoreProps, DispatchProps, WithRouterPathProps {}

export const LessonView = (props: Props) => {
    const {
        canLessonBeChanged,
        clearAllProblemsAnswers,
        clessonId,
        courseId,
        getLessonCertificatesRequest,
        hideNotification,
        isHeadless,
        isPreparedLesson,
        isStatsExpanded,
        lessonHasCertificates,
        lessonStyle,
        match,
        onClose,
        onForceComplete,
        setAssignmentToDefault,
        setShouldShowAssignmentSettings,
        isPutOnBoardEnabled = true,
        shouldShowResultsStub,
        isCopyingEnabled,
        setStatsExpanded,
        shouldCloseOnGoBack,
        shouldShowModal,
        shouldShowOverlay,
        status,
        updateLessonStats,
        setSelectedProblemId,
        hideCloseButton = false,
        hideBanners = false,
        hideFooter = false,
        lessonId,
        loadLessonViewData,
        toggleModal,
        clearEditor,
        dispatch,
        location,
        enableOpeningModalFromQuery,
    } = props;
    const subject = match?.params?.subject;
    const prevSubject = usePrevious(subject);
    const history = useHistory();

    useEffect(() => {
        if (shouldShowModal && shouldCloseOnGoBack) {
            window.history.pushState(null, document.title, window.location.href);
            window.onpopstate = () => {
                closeModal();
            };
        }

        if (!shouldShowModal) {
            window.onpopstate = null;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldShowModal]);

    useEffect(() => {
        if (shouldShowModal) {
            updateLessonStats();
        }
    }, [shouldShowModal, updateLessonStats]);

    useEffect(() => {
        if (!shouldShowModal) {
            setAssignmentToDefault();
            clearAllProblemsAnswers();
            setSelectedProblemId(null);
        }
    }, [shouldShowModal, setAssignmentToDefault, clearAllProblemsAnswers, setSelectedProblemId]);

    useEffect(() => {
        const isCompleted = status && isLessonCompleted(status);

        if (shouldShowModal && clessonId && isCompleted && lessonHasCertificates) {
            getLessonCertificatesRequest(clessonId);
        }
    }, [shouldShowModal, clessonId, lessonHasCertificates, status, getLessonCertificatesRequest]);

    useViewportHack();

    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>): boolean => {
        if (!shouldShowOverlay) {
            return true;
        }

        e.preventDefault();
        e.stopPropagation();

        setShouldShowAssignmentSettings(false);
        setAssignmentToDefault();

        return false;
    };

    const handleCloseModal = () => {
        if (shouldCloseOnGoBack) {
            window.history.back();
        }
        closeModal();
        sendMessageFromIFrame('CLOSE_MODAL', {});
    };

    useEffect(() => {
        if (shouldShowModal && prevSubject && prevSubject !== subject) {
            handleCloseModal();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prevSubject, shouldShowModal, subject]);

    const closeModal = () => {
        if (isStatsExpanded) {
            setStatsExpanded({ isExpanded: false });
            return;
        }

        const { clearLessonResultsState } = props;

        hideNotification();

        ym('reachGoal', 'teacher_lesson_creation_back_to_catalogue ', {
            subject,
            contentType: isPreparedLesson ? LIBRARY_CONTENT_TYPE.PREPARED_LESSON : LIBRARY_CONTENT_TYPE.COURSE_LESSON,
            isCross: 1,
        });

        clearLessonResultsState();

        if (onClose) {
            onClose();
        }

        if (enableOpeningModalFromQuery) {
            // удаляем lessons_id из query при закрытии модалки
            setTimeout(() => {
                const { lesson_id: _, ...restParams } = qs.parse(location.search);
                history.replace({ search: qs.stringify(restParams) });
            });
        }

        if (isHeadless) {
            setIsHeadless(false);
        }
    };

    const handleAttemptCreated = () => {
        setShouldShowAssignmentSettings(false);
    };

    const shouldConnectToSocket = clessonId && courseId && canLessonBeChanged;

    const openLessonModal = useStableCallback(() => {
        const queryParams = qs.parse(location.search);
        const isModalHidedAndLessonIdInQuery = !shouldShowModal && queryParams.lesson_id;
        if (isModalHidedAndLessonIdInQuery) {
            dispatch(clearEditor());
            dispatch(
                loadLessonViewData({
                    courseId,
                    lessonId: Number(queryParams.lesson_id),
                    moduleId: Number(queryParams.module_id),
                })
            );
            dispatch(toggleModal(true));
        }
    });

    // добавляем lessons_id в query при открытии модалки
    useEffect(() => {
        if (enableOpeningModalFromQuery) {
            const queryParams = qs.parse(location.search);
            const isModalOpenedAndLessonIdNotInQuery = shouldShowModal && !queryParams.lesson_id;
            if (lessonId && isModalOpenedAndLessonIdNotInQuery) {
                history.replace({ search: qs.stringify({ ...queryParams, lesson_id: lessonId }) });
            }
        }
    }, [shouldShowModal, location.search, lessonId, history, enableOpeningModalFromQuery]);

    // подгружаем данные урока и открываем модалку при наличии lesson_id в query
    useEffect(() => {
        openLessonModal();
    }, [openLessonModal]);

    return (
        <>
            <Modal
                hasClose={!hideCloseButton}
                mix={b('lesson-view', 'modal', {
                    overlay: shouldShowOverlay,
                    'lesson-style': lessonStyle,
                })}
                onRequestClose={handleCloseModal}
                opened={shouldShowModal}
                useScale={false}
            >
                <div className="lesson-view__wrapper">
                    <div className="lesson-view__side-panel">
                        <SidePanel
                            hideFooter={hideFooter}
                            isCopyingEnabled={isCopyingEnabled}
                            onForceComplete={onForceComplete}
                        />
                        <div className="lesson-view__side-panel-notification-wrapper">
                            <LessonNotification mix="lesson-view__side-panel-notification" />
                        </div>
                    </div>
                    {
                        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                        <div
                            className={b('lesson-view', 'content-wrapper', { overlay: shouldShowOverlay })}
                            onClick={handleOverlayClick}
                        >
                            <LessonNotification mix="lesson-view__content-notification" />
                            <Content
                                hideBanners={hideBanners}
                                isPutOnBoardEnabled={isPutOnBoardEnabled}
                                shouldShowResultsStub={shouldShowResultsStub}
                            />
                        </div>
                    }
                </div>
            </Modal>
            {shouldConnectToSocket && <LessonSocket onAttemptCreated={handleAttemptCreated} />}
        </>
    );
};

const mapStateToProps = (storeState: any) => {
    return {
        clessonId: lessonSelectors.getCLessonId(storeState),
        lessonId: lessonSelectors.getLessonId(storeState),
        shouldShowModal: lessonViewSelectors.getShouldShowModal(storeState),
        courseId: lessonSelectors.getLessonOrCurrentYearCourseId(storeState),
        shouldShowOverlay: selectors.getShouldShowOverlay(storeState),
        status: lessonSelectors.getLessonStatus(storeState),
        isHeadless: lessonViewSelectors.getIsHeadless(storeState),
        isPreparedLesson: lessonSelectors.getIsPreparedLesson(storeState),
        isStatsExpanded: resultsSelectors.getIsStatsExpanded(storeState),
        lessonHasCertificates: lessonSelectors.getCanCertificateBeIssuedForLesson(storeState),
        lessonStyle: lessonSelectors.getLessonStyle(storeState),
        canLessonBeChanged: lessonSelectors.getCanLessonResultBeChanged(storeState),
    };
};

const mapDispatchToProps = (dispatch: Dispatch<Action>) => {
    return bindActionCreators(
        {
            clearAllProblemsAnswers: problemsViewActions.clearAllProblemsAnswers,
            setSelectedProblemId: problemsViewActions.setSelectedProblemId,
            clearLessonResultsState: resultsActions.clearState,
            getLessonCertificatesRequest: certificatesActions.getLessonCertificatesRequest,
            hideNotification: hideNotificationAction,
            setAssignmentToDefault: lessonAssignmentActions.setToDefault,
            setShouldShowAssignmentSettings: lessonViewActions.setShouldShowAssignmentSettings,
            setStatsExpanded: resultsActions.setStatsExpanded,
            updateLessonStats: resultsActions.updateLessonStats,
            setIsHeadless: lessonViewActions.setIsHeadless,
            loadLessonViewData: lessonViewActions.loadLessonViewData,
            clearEditor: lessonActions.clearEditor,
            toggleModal: lessonViewActions.toggleModal,
            dispatch,
        },
        dispatch
    );
};

const wrapped = withRouterPath(connect(mapStateToProps, mapDispatchToProps)(LessonView));
wrapped.displayName = 'LessonView';

export default wrapped;
